import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '../Table/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

const DesignInput = () => {
  const [modules, setModules] = useState('');
  const [rows, setRows] = useState([{ fdnCount: '', noOfStrings: '', defineMatrixRows: '', defineMatrixCols: '', tableType: '', defineTolerance: '' }]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileTableData, setFileTableData] = useState([]);
  const [successMessageInput, setSuccessMessageDesignInput] = useState('');
  const [successMessageUpload, setSuccessMessageUploadFile] = useState('');
  const [successMessageUploaded, setSuccessMessageUploadedFile] = useState('');
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const projectId = localStorage.getItem('selectedProject');

  useEffect(() => {
    fetchUploadedFiles();
  }, []);

  const fetchUploadedFiles = async () => {
    try {
      const response = await axios.get(`${baseURL}get_fdn_file`, {
        params: { projectId }
      });
      setFileTableData(response.data.files);
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
    }
  };

  useEffect(() => {
    fetchDesignInputData();
  }, []);

  const fetchDesignInputData = async () => {
    try {
      const response = await axios.get(`${baseURL}get_design_input`, {
        params: { projectId }
      });

      if (response.data) {
        setModules(response.data.modules || '');
        if (response.data.tableData && response.data.tableData.length > 0) {
          const formattedRows = response.data.tableData.map(row => ({
            fdnCount: row.fdnCount || '',
            noOfStrings: row.noOfStrings || '',
            defineMatrixRows: row.defineMatrixRows || row.defineMatrix.split(' x ')[0] || '',
            defineMatrixCols: row.defineMatrixCols || row.defineMatrix.split(' x ')[1] || '',
            tableType: row.tableType || '',
            defineTolerance: row.defineTolerance || ''
          }));
          setRows(formattedRows);
        }
      }
    } catch (error) {
      console.error('Error fetching design input data:', error);
    }
  };

  const handleFileUpload = (e) => setUploadedFiles(Array.from(e.target.files));

  const addRow = () => rows.length < 3 && setRows([...rows, { fdnCount: '', noOfStrings: '', defineMatrixRows: '', defineMatrixCols: '', tableType: '', defineTolerance: '' }]);

  const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));

  const handleRowChange = (index, field, value) => {
    const newRowData = [...rows];
    newRowData[index][field] = value;
    setRows(newRowData);
  };

  const handleDropdownChange = (index, value) => {
    const newRows = [...rows];
    newRows[index].tableType = value;
    setRows(newRows);
  };

  const getAvailableOptions = (index) => {
    const selectedOptions = rows
      .map((row, i) => (i !== index ? row.tableType : null))
      .filter(Boolean);
    const allOptions = ['Full Table', 'Half Table', 'Quarter Table'];
    return allOptions.filter(option =>
      !selectedOptions.includes(option) || option === rows[index].tableType
    );
  };

  const handleSubmit = async () => {
    const projectId = localStorage.getItem('selectedProject');
    const currentDate = new Date().toISOString();

    const data = {
      projectId,
      modules,
      tableData: rows.map(row => ({
        ...row,
        defineMatrix: `${row.defineMatrixRows} x ${row.defineMatrixCols}`
      })),
      createdBy: sessionStorage.getItem('username'),
      createdDate: currentDate,
      updatedBy: sessionStorage.getItem('username'),
      updatedDate: currentDate
    };

    try {
      const response = await axios.post(`${baseURL}create_or_update_design_input`, data);
      console.log('Data submitted successfully:', response.data);
      setSuccessMessageDesignInput('Table data submitted successfully!');
      fetchDesignInputData();
      setTimeout(() => setSuccessMessageDesignInput(''), 3000);
    } catch (error) {
      console.error('Error submitting data:', error);
    }
  };

  const handleFileSubmit = async () => {
    const data = new FormData();
    const projectId = localStorage.getItem('selectedProject');
    uploadedFiles.forEach(file => data.append('file', file));
    data.append('projectId', projectId);

    try {
      const response = await axios.post(`${baseURL}upload_fdn_csv`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Files uploaded successfully:', response.data);
      setSuccessMessageUploadFile('Files uploaded successfully!');
      fetchUploadedFiles();
      setUploadedFiles([]);
      setTimeout(() => setSuccessMessageUploadFile(''), 3000);
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  const handleFileDelete = async (fileName) => {
    const extractedFileName = fileName.split('/').pop();

    try {
      const response = await axios.delete(`${baseURL}delete_fdn_file`, {
        params: { projectId, fileName: extractedFileName }
      });
      console.log('File deleted successfully:', response.data);
      setSuccessMessageUploadedFile('File deleted successfully!');
      fetchUploadedFiles();
      setTimeout(() => setSuccessMessageUploadedFile(''), 3000);
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const tableHeaders2 = [
    { dbParam: 'srNo', displayName: 'Sr. No' },
    { dbParam: 'uploadedFileName', displayName: 'Uploaded File Name' },
    { dbParam: 'uploadedDate', displayName: 'Uploaded Date' },
    { dbParam: 'download', displayName: 'Download' },
    { dbParam: 'delete', displayName: 'Delete' },
  ];

  const tableData2 = fileTableData.map((file, index) => ({
    srNo: index + 1,
    uploadedFileName: file.file_name,
    uploadedDate: file.last_modified,
    download: file.url ? (
      <a href={file.url} className="text-gray-600 hover:underline">Link</a>
    ) : 'No URL',
    delete: (
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => handleFileDelete(file.file_name)}
        className="text-red-600"
      >
        <FontAwesomeIcon icon={faTrash} />
      </motion.button>
    )
  }));

  return (
    <div className="w-full flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full mx-auto"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-4">Design Input</h1>

        {/* Define Modules Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-gray-50 rounded-xl p-4 mb-2"
        >
          <label className="block text-sm font-bold text-gray-700 mb-2">Number of Modules in a String</label>
          <input
            type="number"
            value={modules}
            onChange={(e) => setModules(e.target.value)}
            className="w-full md:w-1/3 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 bg-white text-gray-800"
            placeholder="Enter number of modules"
          />
        </motion.div>

        {/* Table Definition Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-gray-50 rounded-xl p-6 mb-2"
        >
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Define Table</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#424752] text-white text-sm">
                  {['FDN (Foundation Count)', 'No of Strings', 'Define Matrix', 'Define Tolerance (+/-)', 'Table Type', 'Actions'].map((header, idx) => (
                    <th key={idx} className="p-3 text-left">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {rows.map((row, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white text-sm hover:bg-gray-100"
                    >
                      <td className="p-3">
                        <input
                          type="text"
                          value={row.fdnCount}
                          onChange={(e) => handleRowChange(index, 'fdnCount', e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                          placeholder="FDN Count"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="text"
                          value={row.noOfStrings}
                          onChange={(e) => handleRowChange(index, 'noOfStrings', e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                          placeholder="No of Strings"
                        />
                      </td>
                      <td className="p-3 flex items-center gap-2">
                        <input
                          type="text"
                          value={row.defineMatrixRows}
                          onChange={(e) => handleRowChange(index, 'defineMatrixRows', e.target.value)}
                          className="w-16 p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white text-center"
                          placeholder="Rows"
                        />
                        <span className="text-gray-500">x</span>
                        <input
                          type="text"
                          value={row.defineMatrixCols}
                          onChange={(e) => handleRowChange(index, 'defineMatrixCols', e.target.value)}
                          className="w-16 p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white text-center"
                          placeholder="Cols"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="text"
                          value={row.defineTolerance}
                          onChange={(e) => handleRowChange(index, 'defineTolerance', e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                          placeholder="Tolerance"
                        />
                      </td>
                      <td className="p-3">
                        <select
                          value={row.tableType}
                          onChange={(e) => handleDropdownChange(index, e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        >
                          <option value="">Select Table Type</option>
                          {getAvailableOptions(index).map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3 text-center">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => removeRow(index)}
                          className="text-[#424752]"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          {rows.length < 3 && (
            <div className="mt-4 text-right">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={addRow}
                className="bg-[#424752] text-white px-4 py-2 rounded-lg"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Row
              </motion.button>
            </div>
          )}
          <div className="mt-6 text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSubmit}
              className="bg-[#424752] hover:bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold"
            >
              Submit
            </motion.button>
          </div>
          <AnimatePresence>
            {successMessageInput && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-center text-green-600 font-semibold mt-4"
              >
                {successMessageInput}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* File Upload Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-gray-50 rounded-xl p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Upload File</h2>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <input
              type="file"
              onChange={handleFileUpload}
              className="w-full sm:w-auto p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              multiple
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleFileSubmit}
              className="bg-[#424752] hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold"
            >
              Upload
            </motion.button>
          </div>
          <AnimatePresence>
            {successMessageUpload && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-center text-green-600 font-semibold mt-4"
              >
                {successMessageUpload}
              </motion.div>
            )}
          </AnimatePresence>
          {uploadedFiles.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-sm text-gray-600"
            >
              Selected: {uploadedFiles.map(file => file.name).join(', ')}
            </motion.div>
          )}
        </motion.div>

        {/* Uploaded Files Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <h2 className="w-full text-xl flex justify-center font-semibold text-gray-800 mb-2">Uploaded Files</h2>
          <AnimatePresence>
            {successMessageUploaded && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-center text-green-600 font-semibold mb-4"
              >
                {successMessageUploaded}
              </motion.div>
            )}
          </AnimatePresence>
          <Table
            tableHeaders={tableHeaders2}
            tableData={tableData2}
            initialOrder="asc"
            initialOrderBy=""
            handleEditClick={(row) => console.log('Edit row:', row)}
            rowHighlightCondition={(row) => false}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DesignInput;


// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Table from '../Table/Table';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faTrash } from '@fortawesome/free-solid-svg-icons';

// const DesignInput = () => {
//   const [modules, setModules] = useState('');
//   const [rows, setRows] = useState([{ fdnCount: '', noOfStrings: '', defineMatrixRows: '', defineMatrixCols: '', tableType: '', defineTolerance: '' }]);
//   const [uploadedFiles, setUploadedFiles] = useState([]);
//   const [fileTableData, setFileTableData] = useState([]);
//   const [successMessageInput, setSuccessMessageDesignInput] = useState(''); // State for success message
//   const [successMessageUpload, setSuccessMessageUploadFile] = useState(''); // State for success message
//   const [successMessageUploaded, setSuccessMessageUploadedFile] = useState(''); // State for success message

//   const projectId = localStorage.getItem('selectedProject');

//   useEffect(() => {
//     fetchUploadedFiles();
//   }, []);

//   const fetchUploadedFiles = async () => {
//     try {
//       const response = await axios.get('https://redoms-backend-dev.m-devsecops.com/get_fdn_file', {
//         params: { projectId }
//       });
//       setFileTableData(response.data.files);
//     } catch (error) {
//       console.error('Error fetching uploaded files:', error);
//     }
//   };

//   useEffect(() => {
//     fetchDesignInputData();
//   }, []);

//   const fetchDesignInputData = async () => {
//     try {
//       const response = await axios.get('https://redoms-backend-dev.m-devsecops.com/get_design_input', {
//         params: { projectId }
//       });

//       if (response.data) {
//         setModules(response.data.modules || '');
//         if (response.data.tableData && response.data.tableData.length > 0) {
//           const formattedRows = response.data.tableData.map(row => ({
//             fdnCount: row.fdnCount || '',
//             noOfStrings: row.noOfStrings || '',
//             defineMatrixRows: row.defineMatrixRows || row.defineMatrix.split(' x ')[0] || '',
//             defineMatrixCols: row.defineMatrixCols || row.defineMatrix.split(' x ')[1] || '',
//             tableType: row.tableType || '',
//             defineTolerance: row.defineTolerance || ''
//           }));
//           setRows(formattedRows);
//         }
//       }
//     } catch (error) {
//       console.error('Error fetching design input data:', error);
//     }
//   };

//   const handleFileUpload = (e) => setUploadedFiles(Array.from(e.target.files));

//   const addRow = () => rows.length < 3 && setRows([...rows, { fdnCount: '', noOfStrings: '', defineMatrixRows: '', defineMatrixCols: '', tableType: '', defineTolerance: '' }]);

//   const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));

//   const handleRowChange = (index, field, value) => {
//     const newRowData = [...rows];
//     newRowData[index][field] = value;
//     setRows(newRowData);
//   };

//   const handleDropdownChange = (index, value) => {
//     const newRows = [...rows];
//     newRows[index].tableType = value;
//     setRows(newRows);
//   };

//   const getAvailableOptions = (index) => {
//     const selectedOptions = rows
//       .map((row, i) => (i !== index ? row.tableType : null))
//       .filter(Boolean);
//     const allOptions = ['Full Table', 'Half Table', 'Quarter Table'];
//     return allOptions.filter(option =>
//       !selectedOptions.includes(option) || option === rows[index].tableType
//     );
//   };

//   const handleSubmit = async () => {
//     const projectId = localStorage.getItem('selectedProject');
//     const currentDate = new Date().toISOString();

//     const data = {
//       projectId,
//       modules,
//       tableData: rows.map(row => ({
//         ...row,
//         defineMatrix: `${row.defineMatrixRows} x ${row.defineMatrixCols}`
//       })),
//       createdBy: sessionStorage.getItem('username'),
//       createdDate: currentDate,
//       updatedBy: sessionStorage.getItem('username'),
//       updatedDate: currentDate
//     };

//     try {
//       const response = await axios.post('https://redoms-backend-dev.m-devsecops.com/create_or_update_design_input', data);
//       console.log('Data submitted successfully:', response.data);
//       setSuccessMessageDesignInput('Table data submitted successfully!');
//       fetchDesignInputData();
//       setTimeout(() => setSuccessMessageDesignInput(''), 3000);
//     } catch (error) {
//       console.error('Error submitting data:', error);
//     }
//   };

//   const handleFileSubmit = async () => {
//     const data = new FormData();
//     const projectId = localStorage.getItem('selectedProject');
//     uploadedFiles.forEach(file => data.append('file', file));
//     data.append('projectId', projectId);

//     try {
//       const response = await axios.post('https://redoms-backend-dev.m-devsecops.com/upload_fdn_csv', data, {
//         headers: {
//           'Content-Type': 'multipart/form-data',
//         },
//       });
//       console.log('Files uploaded successfully:', response.data);
//       setSuccessMessageUploadFile('Files uploaded successfully!');
//       fetchUploadedFiles();
//       setUploadedFiles([]); // Clear the file input after upload
//       setTimeout(() => setSuccessMessageUploadFile(''), 3000); // Clear message after 3 seconds
//     } catch (error) {
//       console.error('Error uploading files:', error);
//     }
//   };

//   const handleFileDelete = async (fileName) => {
//     const extractedFileName = fileName.split('/').pop(); // Extract file name only

//     try {
//       const response = await axios.delete('https://redoms-backend-dev.m-devsecops.com/delete_fdn_file', {
//         params: { projectId, fileName: extractedFileName }
//       });
//       console.log('File deleted successfully:', response.data);
//       setSuccessMessageUploadedFile('File deleted successfully!');
//       fetchUploadedFiles(); // Refresh the file list
//       setTimeout(() => setSuccessMessageUploadedFile(''), 3000); // Clear message after 3 seconds
//     } catch (error) {
//       console.error('Error deleting file:', error);
//     }
//   };

//   const tableHeaders2 = [
//     { dbParam: 'srNo', displayName: 'Sr. No' },
//     { dbParam: 'uploadedFileName', displayName: 'Uploaded File Name' },
//     { dbParam: 'uploadedDate', displayName: 'Uploaded Date' },
//     { dbParam: 'download', displayName: 'Download' },
//     { dbParam: 'delete', displayName: 'Delete' },
//   ];

//   const tableData2 = fileTableData.map((file, index) => ({
//     srNo: index + 1,
//     uploadedFileName: file.file_name,
//     uploadedDate: file.last_modified,
//     download: file.url ? (
//       <a href={file.url} className="text-gray-600 hover:underline">Link</a>
//     ) : 'No URL',
//     delete: (
//       <button
//         onClick={() => handleFileDelete(file.file_name)}
//         className="text-red-600"
//       >
//         <FontAwesomeIcon icon={faTrash} />
//       </button>
//     )
//   }));

//   return (
//     <div className="container bg-gray-200 p-4 rounded-lg mx-auto mt-2">
//       <h1 className="text-center text-gray-800 text-3xl font-bold mb-8">DESIGN INPUT</h1>

//       <div className='bg-gray-400 p-6 rounded-lg mb-6'>
//         <div className="mb-5">
//           <label className="block mb-2 text-gray-800 text-lg">Define No. of Modules in a String</label>
//           <input type="number" value={modules} onChange={(e) => setModules(e.target.value)} className="border border-gray-300 p-2 rounded w-[30%]" />
//         </div>

//         <h2 className="text-xl font-semibold text-gray-800 mb-3">Define Table</h2>
//         <table className="w-full mb-3 border-collapse overflow-hidden shadow-md rounded-md">
//           <thead>
//             <tr className="bg-[#424752] text-white text-[13px]">
//               {['FDN (Foundation Count)', 'No of Strings in a Table', 'Define Matrix', 'Define Tolerance (+/-)', 'Table Type', 'Actions'].map((header, idx) => (
//                 <th key={idx} className="p-3">{header}</th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {rows.map((row, index) => (
//               <tr key={index} className="bg-white text-[13px] hover:bg-gray-100">
//                 <td className="p-2">
//                   <input type="text" value={row.fdnCount} onChange={(e) => handleRowChange(index, 'fdnCount', e.target.value)} className="border p-1 rounded w-full" />
//                 </td>
//                 <td className="p-2">
//                   <input type="text" value={row.noOfStrings} onChange={(e) => handleRowChange(index, 'noOfStrings', e.target.value)} className="border p-1 rounded w-full" />
//                 </td>
//                 <td className="p-2 pl-4 pr-2 pt-4 flex items-center gap-2">
//                   <input
//                     type="text"
//                     value={row.defineMatrixRows}
//                     onChange={(e) => handleRowChange(index, 'defineMatrixRows', e.target.value)}
//                     className="border p-1 rounded w-16 text-center"
//                     placeholder="Rows"
//                   />
//                   <span className="text-gray-500">x</span>
//                   <input
//                     type="text"
//                     value={row.defineMatrixCols}
//                     onChange={(e) => handleRowChange(index, 'defineMatrixCols', e.target.value)}
//                     className="border p-1 rounded w-16 text-center"
//                     placeholder="Cols"
//                   />
//                 </td>
//                 <td className="p-2">
//                   <input
//                     type="text"
//                     value={row.defineTolerance}
//                     onChange={(e) => handleRowChange(index, 'defineTolerance', e.target.value)}
//                     className="border p-1 rounded w-full"
//                     placeholder="Tolerance"
//                   />
//                 </td>
//                 <td className="p-2">
//                   <select
//                     value={row.tableType}
//                     onChange={(e) => handleDropdownChange(index, e.target.value)}
//                     className="border p-1 rounded w-full"
//                   >
//                     <option value="">Select Table Type</option>
//                     {getAvailableOptions(index).map(option => (
//                       <option key={option} value={option}>
//                         {option}
//                       </option>
//                     ))}
//                   </select>
//                 </td>
//                 <td className="p-2 text-center">
//                   <button onClick={() => removeRow(index)} className="text-[#424752]"><FontAwesomeIcon icon={faTrash} /></button>
//                 </td>
//               </tr>
//             ))}
//             {rows.length < 3 && (
//               <tr>
//                 <td colSpan="6" className="text-right p-0">
//                   <div className="inline-block">
//                     <button onClick={addRow} className="bg-[#424752] text-white p-2 rounded">Add Row</button>
//                   </div>
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>

//         <div className="text-center mb-5">
//           <button onClick={handleSubmit} className="bg-[#424752] hover:bg-gray-500 text-white mt-0 p-2 rounded w-36">Submit</button>
//         </div>

//         {/* Success Message */}
//         {successMessageInput && (
//           <div className="text-center text-green-600 font-semibold mb-4">
//             {successMessageInput}
//           </div>
//         )}
//       </div>
//       <div className="bg-gray-400 rounded-lg p-4 mb-5">
//         <label className="block text-lg text-gray-800 font-bold">Upload File</label>
//         <div className="flex items-center">
//           <input type="file" onChange={handleFileUpload} className="border border-gray-800 p-2 rounded w-[30%]" multiple />
//           <button onClick={handleFileSubmit} className="bg-[#424752] hover:bg-gray-600 text-white p-3 w-36 rounded ml-3">Upload</button>
//         </div>
//         {successMessageUpload && (
//           <div className="text-center text-green-600 font-semibold mt-2">
//             {successMessageUpload}
//           </div>
//         )}
//       </div>

//       <h2 className="text-xl text-gray-800 font-semibold">Uploaded Files</h2>
//       {successMessageUploaded && (
//         <div className="text-center text-green-600 font-semibold mb-4">
//           {successMessageUploaded}
//         </div>
//       )}
//       <Table
//         tableHeaders={tableHeaders2}
//         tableData={tableData2}
//         initialOrder="asc"
//         initialOrderBy=""
//         handleEditClick={(row) => console.log('Edit row:', row)}
//         rowHighlightCondition={(row) => false}
//       />
//     </div>
//   );
// };

// export default DesignInput;